import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import styled from 'styled-components';
import { Box, Flex, Text, Button } from 'rebass/styled-components';
import { FaInstagram, FaWhatsapp, FaLinkedinIn } from 'react-icons/fa';
import { IoIosArrowRoundUp } from 'react-icons/io';

const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(26, 35, 46, 0.5), rgba(0, 0, 0));
`;

const FooterContent = styled.div`
  position: relative;
  z-index: 2;
`;

const FooterContainer = styled.footer`
  color: #a0a0a0;
  padding: 80px 40px 20px;
  font-family: 'Sk-Modernist-Regular', sans-serif;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 20px 20px;
  }
`;

const HeroSection = styled(Flex)`
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 100px;

  @media (max-width: 768px) {
    margin-bottom: 70px;
  }
`;

const Title = styled.h2`
  color: white;
  font-weight: 400;
  line-height: 1.2;
  margin-bottom: 20px;

  font-size: 64px;

  span {
    color: #ff4f00;
  }

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 34px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const CTAButtons = styled(Flex)`
  gap: 16px;

  @media (max-width: 480px) {
    button {
      width: 100%;
    }
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr) 1.5fr;
  gap: 40px;
  max-width: 1300px;
  margin: 0 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ColumnTitle = styled(Text)`
  color: #666;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  margin-bottom: 20px;
  font-weight: bold;
`;

const FooterLink = styled(GatsbyLink)`
  display: block;
  color: #a0a0a0;
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 10px;

  &:hover {
    color: white;
  }
`;

const Badge = styled.span`
  background: #333;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const ContactColumn = styled(Box)`
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const BottomBar = styled(Flex)`
  max-width: 1300px;
  margin: 60px auto 0;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const ScrollTop = styled.button`
  background: #1a1a1a;
  border: 1px solid #333;
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <BackgroundVideo autoPlay muted loop playsInline>
        <source src="/videos/footer-background.mp4" type="video/mp4" />
      </BackgroundVideo>

      <Overlay />

      <FooterContent>
        <HeroSection>
          <Title>
            Care for <span>Patients</span>,<br />
            not paperwork.
          </Title>

          <Text color="white" mb={4}>
            More time for people, less time on admin
          </Text>

          <CTAButtons>
            <Button
              sx={{
                bg: '#ff4f00',
                color: 'white',
                px: 4,
                py: 2,
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 400,
                textWrap: 'nowrap',
              }}
            >
              Try Now<small style={{ color: '#fffdfd', opacity: 0.8, marginLeft: 8 }}>It's free</small>
            </Button>

            <Button
              sx={{
                bg: 'white',
                color: '#51504e',
                px: 4,
                py: 2,
                borderRadius: 8,
                border: '1px solid #e3e3e3',
                cursor: 'pointer',
                fontWeight: 400,
                textWrap: 'nowrap',
              }}
            >
              Get a Demo
            </Button>
          </CTAButtons>
        </HeroSection>

        <FooterGrid>
          <Box>
            <ColumnTitle>Use Cases</ColumnTitle>
            <FooterLink to="/">Population health</FooterLink>
            <FooterLink to="/">Pharmacy clinics</FooterLink>
            <FooterLink to="/">Primary care</FooterLink>
            <FooterLink to="/">Workplace</FooterLink>
            <FooterLink to="/">
              Home services <Badge>soon</Badge>
            </FooterLink>
            <FooterLink to="/">
              Physiotherapy <Badge>soon</Badge>
            </FooterLink>
          </Box>

          <Box>
            <ColumnTitle>Resources</ColumnTitle>
            <FooterLink to="/">Case studies</FooterLink>
            <FooterLink to="/">Help center</FooterLink>
            <FooterLink to="/">FAQs</FooterLink>
          </Box>

          <Box>
            <ColumnTitle>Company</ColumnTitle>
            <FooterLink to="/">About us</FooterLink>
            <FooterLink to="/">Contact us</FooterLink>
            <FooterLink to="/">Blog</FooterLink>
            <FooterLink to="/">
              Careers <Badge style={{ background: '#ff5c35' }}>10+</Badge>
            </FooterLink>
          </Box>

          <Box>
            <ColumnTitle>Legal</ColumnTitle>
            <FooterLink to="/">Privacy policy</FooterLink>
            <FooterLink to="/">Terms of services</FooterLink>
            <FooterLink to="/">Cookie policy</FooterLink>
            <FooterLink to="/">GDPR policy</FooterLink>
            <FooterLink to="/">Slavery & human trafficking policy</FooterLink>
          </Box>

          <ContactColumn>
            <FooterLink to="/">
              <img src="/images/logoicon.svg" alt="Logo" height="60" />
            </FooterLink>
            <FooterLink to="/">Clive House, Clive Street, Bolton, BL1 1ET</FooterLink>
            <FooterLink to="tel:+442080166020">+44 (0)208 0166 020</FooterLink>
            <FooterLink to="mailto:support@healthya.co.uk">support@healthya.co.uk</FooterLink>
          </ContactColumn>
        </FooterGrid>

        <BottomBar>
          <Flex alignItems="center">
            {/* <FaInstagram size={16} style={{ marginRight: 8 }} />
            <Text mr={4}>Instagram</Text>

            <FaWhatsapp size={16} style={{ marginRight: 8 }} />
            <Text>Whatsapp</Text> */}

            <FooterLink to="https://www.linkedin.com/company/healthya-ltd/" target="_blank" rel="noopener noreferrer">
              <FaLinkedinIn size={16} style={{ marginRight: 8 }} />
              LinkedIn
            </FooterLink>
          </Flex>

          <Text>© 2025 — Copyright</Text>

          <ScrollTop onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <IoIosArrowRoundUp size={20} />
          </ScrollTop>
        </BottomBar>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
